"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUpdatedApprovedListWithoutQueryingContract = exports.clientConnectedToCatalystIn = void 0;
const dcl_catalyst_commons_1 = require("dcl-catalyst-commons");
const CatalystClient_1 = require("../CatalystClient");
const CatalystsList_1 = __importDefault(require("../CatalystsList"));
/**
 * Returns a CatalystClient connected to one of the catalysts in the given network
 */
async function clientConnectedToCatalystIn(network, origin) {
    const noContractList = await getUpdatedApprovedListWithoutQueryingContract({
        preKnownServers: { network },
        origin
    });
    let list;
    if (noContractList) {
        list = noContractList;
    }
    else {
        console.warn('Falling back to the smart contract to get an updated list of active servers');
        list = await getApprovedListFromContract(network);
    }
    const shuffled = shuffleArray(list);
    for (const catalystUrl of shuffled) {
        const client = new CatalystClient_1.CatalystClient(catalystUrl, origin);
        const isUp = await isServerUp(client);
        if (isUp) {
            return client;
        }
    }
    throw new Error(`Couldn't find a server on the ${network} network that was up`);
}
exports.clientConnectedToCatalystIn = clientConnectedToCatalystIn;
async function isServerUp(client) {
    try {
        await Promise.all([client.fetchContentStatus(), client.fetchLambdasStatus()]);
        return true;
    }
    catch (_a) {
        return false;
    }
}
async function getApprovedListFromContract(network) {
    const servers = network === 'mainnet' ? await dcl_catalyst_commons_1.getMainnetCatalysts() : await dcl_catalyst_commons_1.getRopstenCatalysts();
    return servers.map(({ address }) => address);
}
/**
 * The idea here is to build an updated list of the catalysts approved by the DAO, without actually querying the DAO's contract
 * directly. This is because that query is both slow and expensive, so the idea is to use a list of known servers and ask them
 * for the updated list. The thing is this known server list might be outdated, so we need to take that into account. We will
 * take N (defined by REQUIRED_LISTS below) random servers from this known list, and ask them for the currently approved ones. We will then use the intersection of those
 * N lists as the updated list.
 */
const REQUIRED_LISTS = 3;
async function getUpdatedApprovedListWithoutQueryingContract({ preKnownServers, origin, requiredLists, fetchApprovedCatalysts }) {
    // Set defaults if needed
    const catalystListFetch = fetchApprovedCatalysts !== null && fetchApprovedCatalysts !== void 0 ? fetchApprovedCatalysts : ((catalystUrl, origin) => fetchCatalystsApprovedByDAO(catalystUrl, origin));
    const requiredAmountOfLists = requiredLists !== null && requiredLists !== void 0 ? requiredLists : REQUIRED_LISTS;
    // Get the list of known servers
    const knownServers = 'list' in preKnownServers ? preKnownServers.list : CatalystsList_1.default[preKnownServers.network];
    // If I don't know enough servers, then it doesn't make sense to continue
    if (knownServers.length < requiredAmountOfLists) {
        return undefined;
    }
    // Shuffle the list
    const shuffledPreKnownServers = shuffleArray(knownServers);
    // Ask N of them for their list
    const approvedServersList = await Promise.all(shuffledPreKnownServers
        .slice(0, requiredAmountOfLists + 3)
        .map((server) => server.address)
        .map((address) => catalystListFetch(address, origin)));
    // Removed any failures
    const allLists = approvedServersList.filter((approvedServerList) => !!approvedServerList);
    // Check if we need to ask for anyone else's list
    let i = requiredAmountOfLists + 3;
    while (i < shuffledPreKnownServers.length && allLists.length < requiredAmountOfLists) {
        const list = await catalystListFetch(shuffledPreKnownServers[i].address, origin);
        if (list) {
            allLists.push(list);
        }
        i++;
    }
    // If I didn't manage to get al least N lists from different sources, then abort
    if (allLists.length < requiredAmountOfLists) {
        return undefined;
    }
    // Calculate the intersection
    const intersection = calculateIntersection(allLists);
    return intersection.length > 0 ? intersection : undefined;
}
exports.getUpdatedApprovedListWithoutQueryingContract = getUpdatedApprovedListWithoutQueryingContract;
function calculateIntersection(lists) {
    var _a;
    const count = new Map();
    for (const list of lists) {
        for (const element of list) {
            count.set(element, ((_a = count.get(element)) !== null && _a !== void 0 ? _a : 0) + 1);
        }
    }
    return Array.from(count.entries())
        .filter(([_, count]) => count === lists.length)
        .map(([element]) => element);
}
async function fetchCatalystsApprovedByDAO(catalystUrl, origin) {
    const client = new CatalystClient_1.CatalystClient(catalystUrl, origin);
    try {
        const servers = await client.fetchCatalystsApprovedByDAO({ timeout: '10s' });
        return servers.map(({ address }) => address);
    }
    catch (_a) {
        return undefined;
    }
}
function shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}
//# sourceMappingURL=CatalystClientBuilder.js.map